import React from 'react';
import styled from '@emotion/styled';
import Button from '../../components/common/Button';
import authService from '../../services/api/auth';
import {
  useRecaptchaToken,
  isCaptchaRequired,
  RECAPTCHA_SECURITY_ERROR,
} from '../../components/helpers/recaptchaUtils';
import { screenSizes } from 'src/assets/styles/Literals';
import ForgotPasswordModal from '../../containers/auth/ForgotPasswordModal';

const PageWrapper = styled.section`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 16px;
  background: linear-gradient(140deg, #08172a 0%, #0c2b4a 45%, #1d6fa3 100%);

  .login-card {
    width: 100%;
    max-width: 420px;
    padding: 40px 36px 48px;
    background: rgba(255, 255, 255, 0.97);
    border-radius: 18px;
    box-shadow: 0 25px 70px rgba(0, 0, 0, 0.35);
  }

  .login-title {
    margin: 0;
    text-align: center;
    font-size: 2rem;
    color: #0c2b4a;
    font-weight: 700;
  }

  .login-subtitle {
    margin: 10px 0;
    text-align: center;
    color: #63758b;
    font-size: 0.95rem;
  }

  .styled-form {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  .auth-input {
    width: 100%;
    padding: 14px 16px;
    border-radius: 10px;
    border: 1px solid #d9e1ec;
    font-size: 0.95rem;
    background-color: #f6f8fb;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:focus {
      outline: none;
      border-color: #1d6fa3;
      box-shadow: 0 0 0 3px rgba(29, 111, 163, 0.12);
      background-color: #fff;
    }
  }

  .password-field {
    position: relative;
  }

  .toggle-password {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    border: none;
    background: none;
    color: #0a5ea8;
    font-weight: 600;
    cursor: pointer;
  }

  .error-message {
    margin: -4px 0 0;
    color: #dc3545;
    text-align: center;
    font-weight: 600;
  }

  .forgot-password {
    border: none;
    background: none;
    color: #0a5ea8;
    cursor: pointer;
    text-decoration: underline;
    font-weight: 600;
    padding: 0;
  }

  .submit-wrapper {
    margin-top: 10px;
  }

  @media only screen and (max-width: ${screenSizes.tablet}) {
    .login-card {
      padding: 40px 15px;
    }
  }
`;

const initialInputs = {
  email: '',
  password: '',
};

const Login = (props) => {
  const [inputs, setInputs] = React.useState(initialInputs);
  const [errorData, set_errorData] = React.useState(null);
  const [loading, set_loading] = React.useState(false);
  const [showPassword, set_showPassword] = React.useState(false);
  const [isForgotPasswordOpen, set_isForgotPasswordOpen] = React.useState(false);
  const getCaptchaToken = useRecaptchaToken('login');

  React.useEffect(() => {
    if (!errorData) return;
    const timeout = setTimeout(() => set_errorData(null), 3000);
    return () => clearTimeout(timeout);
  }, [errorData]);

  React.useEffect(() => {
    if (localStorage.getItem('userToken')) {
      authService.logout();
    }
  }, []);

  const handleInputs = (event) => {
    const { name, value } = event.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const login = async (event) => {
    event?.preventDefault();
    const { email, password } = inputs;

    if (!email || !password) {
      set_errorData('Todos los campos son obligatorios');
      return;
    }

    set_errorData(null);
    set_loading(true);
    try {
      const captchaRequired = isCaptchaRequired();
      const captcha = await getCaptchaToken({ skip: !captchaRequired });
      if (captchaRequired && !captcha) {
        set_errorData(RECAPTCHA_SECURITY_ERROR);
        set_loading(false);
        return;
      }

      const response = await authService.login({ email, password, captcha });
      set_loading(false);
      if (response.data.success) {
        localStorage.setItem('userToken', response.data.token);
      }
    } catch (error) {
      if (error.response) set_errorData(error.response.data.message);
      set_loading(false);
    }
  };

  const isSubmitDisabled = React.useMemo(() => {
    if (!inputs.email || !inputs.password) return true;
    if (loading) return true;
    return false;
  }, [inputs, loading]);

  return (
    <PageWrapper>
      <div className="login-card">
        <h1 className="login-title">Inicia sesión</h1>
        <p className="login-subtitle">Este login es para el staff de la plataforma</p>
        <form onSubmit={login} className="styled-form Registration__form" noValidate>
          <input
            className="auth-input"
            placeholder="Correo electrónico"
            name="email"
            autoComplete="username"
            value={inputs.email}
            onChange={handleInputs}
          />

          <div className="password-field">
            <input
              className="auth-input"
              onChange={handleInputs}
              name="password"
              value={inputs.password}
              placeholder="Contraseña"
              autoComplete="current-password"
              type={showPassword ? 'text' : 'password'}
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => set_showPassword((prev) => !prev)}
            >
              {showPassword ? 'Ocultar' : 'Ver'}
            </button>
          </div>

          {errorData && <p className="error-message">{errorData}</p>}

          <button
            type="button"
            className="forgot-password"
            onClick={() => set_isForgotPasswordOpen(true)}
          >
            Olvidé mi contraseña
          </button>

          <div className="submit-wrapper">
            <Button
              text="Entrar"
              type="submit"
              disabled={isSubmitDisabled}
              style={{ width: '100%' }}
            />
          </div>
        </form>
        <ForgotPasswordModal
          isOpen={isForgotPasswordOpen}
          onClose={() => set_isForgotPasswordOpen(false)}
        />
      </div>
    </PageWrapper>
  );
};
export default Login;
